import { clearAuthFromExtension, syncAuthToExtension } from '@/lib/extension-bridge'
import type { User } from '@/types'
import Clarity from '@microsoft/clarity'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  setAuth: (user: User, token: string) => void
  updateUser: (user: User | null) => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => {
        localStorage.setItem('auth_token', token)
        set({ user, token, isAuthenticated: true })
        syncAuthToExtension(token)
        // Identify user in Clarity for session tracking (production only —
        // Clarity is only initialized when VITE_ENV=production, and this must
        // match so staging never sends user identity to the prod workspace).
        if (import.meta.env.VITE_ENV === 'production') {
          Clarity.identify(
            String(user.id),
            undefined,
            undefined,
            user.full_name || user.first_name || user.email,
          )
        }
      },
      updateUser: (user) => set({ user }),
      clearAuth: () => {
        localStorage.removeItem('auth_token')
        set({ user: null, token: null, isAuthenticated: false })
        clearAuthFromExtension()
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
)
